/**
 * index.js — Entry point del generador PDF corporativo
 *
 * Tipos: PRESUPUESTO, ORDEN_SERVICIO, INFORME_TECNICO, COMPROBANTE, PRESUPUESTO_VENTA
 * API: generarPDF({ tipo, cliente, sede, tecnico, ticketItems, ... })
 */
import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';
import { procesarFecha, generarNroDocumento } from './theme.js';
import { dibujarFooter } from './layout.js';
import { resetFotosConError, getFotosConError, cargarFoto, precargarFotos } from './helpers.js';
import { detectarTipo, getLabelTipo, getPrefijoNro } from './pdfShared.js';
import { guardarPDF } from './entregar.js';
import { cabecera } from './v2/base.js';
import { generarUnEquipoV2 } from './v2/unEquipo.js';
import { generarVariosV2 } from './v2/varios.js';
import { generarVentaV2 } from './v2/venta.js';

// Diseño limpio (18-oct-2026): título y subtítulo de la cabecera según el documento
const TITULOS = {
    PRESUPUESTO: 'Presupuesto', PRESUPUESTO_VENTA: 'Presupuesto de venta', ORDEN_SERVICIO: 'Trabajo realizado',
    COMPROBANTE: 'Comprobante de venta', INFORME_TECNICO: 'Informe técnico',
};
const masDias = (ddmmaaaa, dias) => {
    const [d, m, a] = String(ddmmaaaa).split('/').map(Number);
    if (!d || !m || !a) return null;
    const f = new Date(a, m - 1, d + dias);
    return `${String(f.getDate()).padStart(2, '0')}/${String(f.getMonth() + 1).padStart(2, '0')}/${f.getFullYear()}`;
};
// % de descuento pagando en efectivo (Configuración); se pide una vez por sesión
let pctEfectivoCache = null;
export async function pctEfectivo() {
    if (pctEfectivoCache != null) return pctEfectivoCache;
    try {
        const { default: api } = await import('../../services/api');
        const r = await Promise.race([api.get('/configuracion'), new Promise((_, no) => setTimeout(() => no(new Error('tarde')), 4000))]);
        const v = Number(r.data?.descuentoEfectivo);
        pctEfectivoCache = Number.isFinite(v) ? v : 10;
    } catch { return 10; }
    return pctEfectivoCache;
}

/** Al abrir la ficha de un trabajo: baja sus fotos y la configuración para que el PDF salga al toque. */
export function prepararPDF(s) {
    if (!s) return;
    const fotos = [...(s.fotosTrabajo || [])];
    (s.items || []).forEach(it => fotos.push(it.fotoAntes, it.fotoDespues, ...(it.fotosExtra || [])));
    precargarFotos(fotos.filter(Boolean));
    pctEfectivo();
}

export const generarPDF = async ({
    tipo               = null,
    esPresupuesto      = false,
    esTecnicoForzado   = null,
    cliente,
    sede,
    tecnico,
    ticketItems        = [],
    fechaServicio,
    descuentoPorcentaje = 0,
    descuentoAlcance   = 'TOTAL',
    totales            = null,   // desglose del backend (servicio.totales); si viene, el PDF no recalcula
    leyenda            = '',
    servicioId         = null,
    nroDocumentoExistente = null,
    firmaCliente       = null,
    firmaTecnico       = null,
    proximoMantenimiento = null,
    garantiaTexto      = null,
    estadoFinal        = null,
    incluirFirmas      = true,
    aclaracionCliente  = '',
    aclaracionTecnico  = '',   // si no viene, va el nombre del técnico
    sinPrecios         = false,
    fechaVisita        = null,
    fotosTrabajo       = [],
    soloResumen        = false,   // muchos equipos: solo el resumen, sin el anexo de fotos
    modalidadCobro     = null,    // EFECTIVO_SIN_FACTURA: el total va con el descuento aplicado y sin IVA
    montoFinal         = null,    // lo que se cobró de verdad (si se cargó)
}) => {
    if (!cliente || ticketItems.length === 0) {
        return toast.error('Datos insuficientes para generar el PDF.');
    }
    resetFotosConError();

    const tipoDetectado = detectarTipo({ tipo, esPresupuesto, ticketItems, esTecnicoForzado });
    const esMulti       = ticketItems.length > 1;
    const doc           = new jsPDF();
    const fecha         = procesarFecha(fechaServicio);
    const prefijo       = getPrefijoNro(tipoDetectado);
    const tipoLabel     = getLabelTipo(tipoDetectado, esMulti);

    // Reusar numero existente
    const nroDocGuardado = nroDocumentoExistente
        || (servicioId ? localStorage.getItem(`pdf_nro_${servicioId}`) : null);
    // El número se mantiene, pero con el prefijo del tipo actual (un presupuesto
    // impreso antes como "OS-…" sale "PP-…", y al revés cuando ya está hecho)
    const nroDoc = nroDocGuardado
        ? String(nroDocGuardado).replace(/^[A-Z]{2}-/, `${prefijo}-`)
        : generarNroDocumento(prefijo, fecha, tecnico || 'TEC');

    if (servicioId && !nroDocGuardado) {
        try {
            const { default: api } = await import('../../services/api');
            api.patch(`/servicios/${servicioId}/nro-doc`, { nroDocumento: nroDoc }).catch(() => {});
        } catch {}
        localStorage.setItem(`pdf_nro_${servicioId}`, nroDoc);
    }

    // Cabecera: el logo y qué documento es (18-oct-2026)
    const esPresup = tipoDetectado === 'PRESUPUESTO' || tipoDetectado === 'PRESUPUESTO_VENTA';
    const valido = esPresup ? masDias(fecha, 7) : null;
    const ctx = {
        titulo: TITULOS[tipoDetectado] || tipoLabel,
        sub: [`N° ${nroDoc}`, fecha, valido ? `Válido hasta ${valido}`
            : (tecnico && !/^mostrador$/i.test(String(tecnico).trim()) ? `Técnico: ${tecnico}` : null)].filter(Boolean).join('  ·  '),
    };
    let y = cabecera(doc, ctx);

    // Solo mostrar sección firmas si hay firma real (no cuadros vacíos)
    const firmasReales = !!(firmaCliente || firmaTecnico);
    const incluirFirmasFinal = incluirFirmas && firmasReales;

    // Dispatch por tipo
    // Fotos del trabajo (14-oct-2026): hasta 4, se cargan en paralelo
    const fotosTrabajoPdf = (await Promise.all((fotosTrabajo || []).slice(0, 4).map(f => cargarFoto(f).catch(() => null)))).filter(Boolean);

    const commonArgs = {
        cliente, sede, tecnico, fecha, nroDoc,
        firmaCliente:  incluirFirmasFinal ? firmaCliente  : null,
        firmaTecnico:  incluirFirmasFinal ? firmaTecnico  : null,
        incluirFirmas: incluirFirmasFinal, aclaracionCliente,
        aclaracionTecnico: aclaracionTecnico || (firmaTecnico ? tecnico || '' : ''),
        descuentoPorcentaje, descuentoAlcance, totales, garantiaTexto,
        proximoMantenimiento, leyenda, sinPrecios, fechaVisita, fotosTrabajo: fotosTrabajoPdf,
        // IVA según el cliente y efectivo (18-oct-2026)
        ctx, condicionIva: cliente.condicionFiscal || cliente.condicionIva || null,
        pctEfectivo: await pctEfectivo(),
        cobradoEfectivo: modalidadCobro === 'EFECTIVO_SIN_FACTURA', montoCobrado: montoFinal,
    };

    if (tipoDetectado === 'COMPROBANTE' || tipoDetectado === 'PRESUPUESTO_VENTA') {
        await generarVentaV2(doc, { ...commonArgs, ticketItems, y, esPresupuesto: tipoDetectado === 'PRESUPUESTO_VENTA' });
    } else if (esMulti) {
        await generarVariosV2(doc, { ...commonArgs, ticketItems, y, esPresupuesto: tipoDetectado === 'PRESUPUESTO', soloResumen });
    } else {
        await generarUnEquipoV2(doc, { ...commonArgs, item: ticketItems[0], y, esPresupuesto: tipoDetectado === 'PRESUPUESTO' });
    }

    // Footer en todas las paginas
    const esOrdenFinal      = tipoDetectado === 'ORDEN_SERVICIO';
    const esTipoPresupuesto = tipoDetectado === 'PRESUPUESTO';
    const esVentaTipo       = tipoDetectado === 'PRESUPUESTO_VENTA' || tipoDetectado === 'COMPROBANTE';
    const leyendaLimpia = (esOrdenFinal || esTipoPresupuesto || esVentaTipo)
        ? null
        : (leyenda || '').replace(/[\r\n]+/g, ' ').trim().substring(0, 110) || null;
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        dibujarFooter(doc, { pagina: i, totalPaginas: total, textoCentral: leyendaLimpia, conEstrellas: esOrdenFinal });
    }

    // Guardar
    const nombreBase = tipoLabel.split(' ')[0];
    const nombreCliente = (cliente.nombre || 'Cliente').replace(/\s+/g, '-');
    const fechaStr = fecha.replace(/\//g, '-');
    const fileName = `${nombreBase}_${nombreCliente}_${fechaStr}.pdf`;

    guardarPDF(doc, fileName);   // 28-oct-2026: con botón si el toque ya venció (pdf/entregar.js)

    const fotosErr = getFotosConError();
    if (fotosErr > 0) {
        toast.error(`⚠ ${fotosErr} foto${fotosErr > 1 ? 's' : ''} no se ${fotosErr > 1 ? 'pudieron' : 'pudo'} incluir en el PDF`, { duration: 5000 });
    }
};
